'use client';

import React, { memo, useCallback } from 'react';
import { motion } from 'motion/react';

import { Separator } from '@repo/ui/components/ui/separator';
import { cn } from '@repo/ui/lib/utils';

import { ArticleCardData } from '@/hooks/api/article/model';
import { usePostMessage } from '@/providers/post-message-provider';

import { ArticleCardCrediblityChart } from './article-card-charts';
import { ArticleCardHeader } from './article-card-header';
import { ArticleCardLabels } from './article-card-labels';
import { ArticleCardMetricsProgressBars } from './article-card-metrics-progress-bars';
import { ArticleCardSummary } from './article-card-summary';
import { ArticleCardTitle } from './article-card-title';

interface ArticleCardProps {
  article: ArticleCardData;
  listOrientation?: 'horizontal' | 'vertical';
  className?: string;
  hideAnchor?: boolean;
  isHackerNews?: boolean;
}

const PureArticleCard: React.FC<ArticleCardProps> = ({
  article,
  listOrientation = 'vertical',
  className,
  hideAnchor = false,
  isHackerNews = false,
}) => {
  const { sendRedirectMessage } = usePostMessage();

  const redirectToArticle = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Detect if user intends to open link in new tab
      const shouldOpenInNewTab =
        e.ctrlKey || // Ctrl+click on Windows/Linux
        e.metaKey || // Cmd+click on macOS
        e.shiftKey || // Shift+click (opens in new window/tab)
        e.button === 1; // Middle mouse button click

      const shouldSendTheMessage = e.button === 0 || shouldOpenInNewTab;
      if (shouldSendTheMessage) {
        sendRedirectMessage(article.url, shouldOpenInNewTab);
      }
    },
    [article.url, sendRedirectMessage],
  );

  return (
    <motion.div
      initial={
        listOrientation === 'vertical' ? { opacity: 0, y: 20 } : { opacity: 0 }
      }
      animate={
        listOrientation === 'vertical' ? { opacity: 1, y: 0 } : { opacity: 1 }
      }
      transition={
        listOrientation === 'vertical'
          ? {
              duration: 0.3,
            }
          : {
              type: 'spring',
              stiffness: 300,
              damping: 20,
            }
      }
      role="link"
      title={article.url}
      onMouseDown={redirectToArticle}
      className={cn(
        'flex flex-1 cursor-pointer flex-col justify-between gap-2 overflow-hidden rounded-[4px] bg-white py-2 transition-[color,background-color,box-shadow,border-color,border-width] duration-200 hover:shadow-[0px_0px_6px_0px_rgba(0,0,0,0.3)] cred-md:gap-[9.3px] cred-md:py-[9.3px] cred-lg:gap-[10.13px] cred-lg:py-[10.13px] cred-xl:gap-[13.33px] cred-xl:py-[13.33px] md:gap-4 data-[state=open]:shadow-[0px_0px_6px_0px_rgba(0,0,0,0.3)] border border-transparent data-[state=open]:border-facts-pie-chart',
        className,
      )}
    >
      <div className="flex w-full flex-1 flex-col justify-between gap-2 cred-md:gap-[9.3px] cred-xl:gap-[13.33px]">
        {/* Brand, Published at, Share, Open in new tab */}
        <div className="space-y-[5.5px] cred-md:space-y-[7px] cred-xl:space-y-2.5 px-2 cred-md:px-[9.3px] cred-lg:px-[10.13px]">
          <ArticleCardHeader
            article={article}
            hideAnchor={hideAnchor}
            isHackerNews={isHackerNews}
          />

          <Separator className="h-[.5px] bg-[#C0C0C0] cred-md:h-[.7px] cred-lg:h-[.85px] cred-xl:h-px" />
        </div>

        {/* Title, Credibility score (mobile) */}
        <div className="flex items-center justify-between gap-1 cred-md:gap-[7px] cred-lg:gap-[7.6px] cred-xl:gap-2.5 px-2 cred-md:px-[9.3px] cred-lg:px-[10.13px]">
          {/* Title */}
          <ArticleCardTitle title={article.title} />

          {/* Credibility score */}
          {article.metrics && (
            <ArticleCardCrediblityChart
              credibilityScore={article.metrics.credibilityScore}
              credibilityReasoning={article.metrics.credibilityReasoning}
            />
          )}
        </div>

        {/* Title, Credibility score, Bias score, Labels (desktop) */}
        <div className="flex w-full flex-1 flex-col justify-between gap-2 cred-md:gap-[9.3px] cred-xl:gap-[13.33px] px-2 cred-md:px-[9.3px] cred-lg:px-[10.13px]">
          {/* Labels */}
          <ArticleCardLabels labels={article.labels} />

          {/* Summary */}
          <ArticleCardSummary
            summary={article.summary ?? undefined}
            aiSummary={article.metrics?.aiSummary ?? undefined}
          />

          {/* Metrics progress bars */}
          <ArticleCardMetricsProgressBars
            claimedFactsScores={
              article.metrics?.claimedFactsScores ?? undefined
            }
            biasScore={article.metrics?.biasScore ?? undefined}
          />
        </div>
        {/* <ArticleCardActionButtons
          keyClaimsCount={article.entityKeyClaims?.length ?? 0}
          // openKeyClaimsClick={handleOpenKeyClaims}
          commentsCount={article.aiGeneratedCommentsCount ?? 0}
          // openCommentsClick={handleOpenComments}
        /> */}
      </div>
    </motion.div>
  );
};
export const ArticleCard = memo(PureArticleCard);
